import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { CAL_BOOKING_URL } from '../constants/booking';

const serviceLinks = [
  {
    name: 'All Services',
    href: '/services',
    description: 'Web development, IT consulting, cloud, cybersecurity & SEO',
  },
  {
    name: 'Systems We Build',
    href: '/systems',
    description: 'Custom software for firms, companies & government',
  },
  {
    name: 'Learn More',
    href: '/learn-more',
    description: 'How we work with businesses across South Africa',
  },
];

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Special Offers', href: '/special-offers' },
  { name: 'Blog', href: '/blog' },
  { name: 'Why Us', href: '/why-us' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setServicesExpanded(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isServicesActive = serviceLinks.some((link) => isActive(link.href));
  const closeMenu = useCallback(() => setIsOpen(false), []);

  const linkClass = (active) =>
    `${active ? 'text-primary' : 'text-black hover:text-secondary'} uppercase tracking-wide text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary rounded-md px-1`;

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/80'}`}
      aria-label="Main navigation"
    >
      <div className="nav-container">
        <div className="flex items-center justify-between h-20 md:h-24">
          <div className="flex-shrink-0 -ml-2 sm:-ml-4">
            <Link to="/" className="block" aria-label="MbheTech Solutions home">
              <img
                src="/logo.png"
                alt="MbheTech Solutions"
                className="h-9 md:h-12 w-auto object-contain"
                width={160}
                height={48}
              />
            </Link>
          </div>

          <div className="hidden md:flex flex-grow">
            <div className="mx-auto max-w-3xl flex-1">
              <div className="border-2 border-blue-600 rounded-full px-5 lg:px-7 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <Link to="/" className={linkClass(isActive('/'))}>
                    Home
                  </Link>
                  <Link to="/about" className={linkClass(isActive('/about'))}>
                    About
                  </Link>

                  {/* Services dropdown */}
                  <div className="relative group">
                    <Link
                      to="/services"
                      className={`inline-flex items-center gap-0.5 ${linkClass(isServicesActive)}`}
                      aria-haspopup="true"
                    >
                      Services
                      <ChevronDownIcon
                        className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180"
                        aria-hidden
                      />
                    </Link>

                    <div
                      className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100"
                      role="menu"
                      aria-label="Services menu"
                    >
                      <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-xl">
                        <div className="border-b border-slate-100 bg-blue-50 px-4 py-3">
                          <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                            Explore our services
                          </p>
                        </div>
                        <ul className="py-2">
                          {serviceLinks.map((item) => (
                            <li key={item.href}>
                              <Link
                                to={item.href}
                                role="menuitem"
                                className={`block px-4 py-3 transition-colors hover:bg-blue-50 ${
                                  isActive(item.href) ? 'bg-blue-50' : ''
                                }`}
                              >
                                <span className="block text-sm font-bold uppercase tracking-wide text-slate-900">
                                  {item.name}
                                </span>
                                <span className="mt-1 block text-xs normal-case leading-snug text-slate-600">
                                  {item.description}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {navLinks.slice(2).map((link) => (
                    <Link key={link.name} to={link.href} className={linkClass(isActive(link.href))}>
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <div className="pl-4">
              <a
                href={CAL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold bg-[#0A66C2] hover:bg-[#084b92] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary"
              >
                Book Consultation
              </a>
            </div>
          </div>

          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="text-black hover:text-gray-600 p-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
              aria-expanded={isOpen}
              aria-controls="mobile-nav-menu"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden pb-4" id="mobile-nav-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg rounded-2xl mt-2 border-2 border-blue-600 max-h-[70vh] overflow-y-auto">
              <Link
                to="/"
                className={`block px-4 py-3 rounded-md ${
                  isActive('/') ? 'bg-blue-50 text-primary' : 'text-black hover:bg-gray-50'
                } uppercase tracking-wide text-sm font-semibold`}
                onClick={closeMenu}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`block px-4 py-3 rounded-md ${
                  isActive('/about') ? 'bg-blue-50 text-primary' : 'text-black hover:bg-gray-50'
                } uppercase tracking-wide text-sm font-semibold`}
                onClick={closeMenu}
              >
                About
              </Link>

              {/* Mobile Services accordion */}
              <div className="rounded-md border border-slate-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setServicesExpanded((prev) => !prev)}
                  className={`flex w-full items-center justify-between px-4 py-3 text-left uppercase tracking-wide text-sm font-semibold ${
                    isServicesActive ? 'bg-blue-50 text-primary' : 'text-black hover:bg-gray-50'
                  }`}
                  aria-expanded={servicesExpanded}
                >
                  Services
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${servicesExpanded ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {servicesExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50">
                    {serviceLinks.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`block px-4 py-3 border-b border-slate-100 last:border-b-0 ${
                          isActive(item.href) ? 'bg-blue-50 text-primary' : 'text-black hover:bg-white'
                        }`}
                        onClick={closeMenu}
                      >
                        <span className="block text-sm font-bold">{item.name}</span>
                        <span className="mt-0.5 block text-xs font-normal normal-case text-slate-600">
                          {item.description}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block px-4 py-3 rounded-md ${
                    isActive(link.href) ? 'bg-blue-50 text-primary' : 'text-black hover:bg-gray-50'
                  } uppercase tracking-wide text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary`}
                  onClick={closeMenu}
                >
                  {link.name}
                </Link>
              ))}

              <a
                href={CAL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 rounded-md text-white bg-[#0A66C2] hover:bg-[#084b92] uppercase tracking-wide text-sm font-semibold text-center"
                onClick={closeMenu}
              >
                Book Consultation
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
